// Pure helpers for the "Revenue & Profit" dashboard widget. Kept free of
// React/redux so the aggregation can be unit tested in isolation (mirrors
// newCustomersSecuredHelpers / customerPerformanceHelpers).
//
// Business logic: actual Revenue (excl. tax) and *actual* Profit (finalized
// costs — shipping, labor, carpet, etc.) from INVOICED jobs only, bucketed into
// Month-To-Date and Year-To-Date, overall and per salesperson.
//
// Live data comes from the PostedSalesInvoices endpoint: the widget fetches it
// and passes each posted-invoice row through `mapPostedSalesInvoiceRows` (below)
// into the record shape this aggregation consumes, so the summarization stays
// agnostic of the data source and remains unit-testable on its own.

import { getPeriodRanges, toDay } from "./newCustomersSecuredHelpers";

// Reuse the shared period windows (MTD / YTD) so the date logic lives in one place.
export { getPeriodRanges } from "./newCustomersSecuredHelpers";

const inRange = (day, range) => day >= range.fromDate && day <= range.toDate;

// Format a number as USD with cents (e.g. 3500 -> "$3,500.00"). Cents are shown
// so the rounded per-salesperson rows always sum to the displayed total (whole-
// dollar rounding rounded each value independently and could mis-foot by a few
// dollars).
export const formatCurrency = (value) => {
  const n = Number(value) || 0;
  return n.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

// Summarize invoiced-job revenue & profit from raw records. Each record:
//   { salesPerson, invoicedDate, revenue, actualProfit }
// Returns:
//   totals: { revenueMtd, revenueYtd, profitMtd, profitYtd }
//   reps:   [{ repName, revenueMtd, revenueYtd, profitMtd, profitYtd }] (YTD desc)
// Records outside the year-to-date window (or without a usable date) are ignored.
export const summarizeRevenueProfit = (records, now = new Date()) => {
  const ranges = getPeriodRanges(now);
  const totals = { revenueMtd: 0, revenueYtd: 0, profitMtd: 0, profitYtd: 0 };
  const repMap = new Map();
  records = records.filter(item => item.division?.trim().toLowerCase() !== "commercial"); // remove commercial plans
  (Array.isArray(records) ? records : []).forEach((r) => {
    if (!r) return;
    const day = toDay(r.invoicedDate);
    if (!day || !inRange(day, ranges.year)) return; // YTD bounds everything
    const revenue = Number(r.revenue) || 0;
    const profit = Number(r.actualProfit) || 0;
    const isMonth = inRange(day, ranges.month);
    const repName = (r.salesPerson || "").trim() || "Unassigned";

    const rep =
      repMap.get(repName) ||
      { repName, revenueMtd: 0, revenueYtd: 0, profitMtd: 0, profitYtd: 0 };

    totals.revenueYtd += revenue;
    totals.profitYtd += profit;
    rep.revenueYtd += revenue;
    rep.profitYtd += profit;
    if (isMonth) {
      totals.revenueMtd += revenue;
      totals.profitMtd += profit;
      rep.revenueMtd += revenue;
      rep.profitMtd += profit;
    }
    repMap.set(repName, rep);
  });

  const reps = Array.from(repMap.values()).sort(
    (a, b) => b.revenueYtd - a.revenueYtd || a.repName.localeCompare(b.repName)
  );

  return { totals, reps };
};

// ---------------------------------------------------------------------------
// Live data mapper. The "Revenue & Profit" widget fetches the PostedSalesInvoices
// endpoint and runs its `result` rows through this to get the record shape
// summarizeRevenueProfit consumes. Kept pure (no axios/config) so it stays unit-
// testable and the network concern lives in the widget.
// ---------------------------------------------------------------------------

// Map raw PostedSalesInvoices rows to invoiced-job records. Each API row:
//   { invoiceNo, salespersonName, postingDate, sales, margin, ratio,
//     locationCode, division }
// becomes { salesPerson, invoicedDate, revenue, actualProfit } where revenue is
// `sales` and actual profit is `margin`. A blank salespersonName is preserved as
// "" (summarizeRevenueProfit buckets those under "Unassigned"); non-array input
// yields [] and falsy rows are skipped.
export const mapPostedSalesInvoiceRows = (rows) =>
  (Array.isArray(rows) ? rows : []).reduce((acc, r) => {
    if (!r) return acc;
    acc.push({
      salesPerson: (r.salespersonName || "").trim(),
      invoicedDate: r.postingDate,
      revenue: Number(r.sales) || 0,
      actualProfit: Number(r.margin) || 0,
    });
    return acc;
  }, []);
